import { X, Clock, Calendar, User, Building2, Play, Check, Loader2 } from 'lucide-react'
import { Button, Card, CardHeader, CardTitle, CardContent, ScrollArea } from '@/components/ui'
import { TaskStatusBadge } from './TaskStatusBadge'
import { cn, formatDate, formatDuration, daysUntil, getDeadlineColor } from '@/lib/utils'
import { useChangeTaskStatus } from '@/hooks'
import { useTimerStore } from '@/stores'
import type { Task, TaskStatus as TaskStatusType } from '@/api'

interface TaskDetailProps {
  task: Task
  onClose: () => void
  onStatusChange?: (task: Task) => void
}

const statusOptions: { value: TaskStatusType; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
]

export function TaskDetail({ task, onClose, onStatusChange }: TaskDetailProps) {
  const daysRemaining = task.due_date ? daysUntil(task.due_date) : null
  const changeStatus = useChangeTaskStatus()
  const { startTimer, isRunning, taskId: activeTaskId } = useTimerStore()

  const isTimerActive = isRunning && activeTaskId === task.id

  const handleStatusChange = (status: TaskStatusType) => {
    if (status !== task.status) {
      changeStatus.mutate(
        { id: task.id, status },
        { onSuccess: (updatedTask) => onStatusChange?.(updatedTask as Task) }
      )
    }
  }

  const handleStartTimer = () => {
    if (!isTimerActive) {
      startTimer(task.id, task.title)
    }
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
        <div className="space-y-2 flex-1 pr-4">
          <CardTitle className="text-lg leading-tight">{task.title}</CardTitle>
          <TaskStatusBadge status={task.status} />
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden">
        <ScrollArea className="h-full pr-4">
          <div className="space-y-6">
            {/* Description */}
            {task.description && (
              <div>
                <h4 className="text-sm font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {task.description}
                </p>
              </div>
            )}

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Due date */}
              {task.due_date && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Due Date</h4>
                  <div className={cn('flex items-center gap-1.5', getDeadlineColor(daysRemaining!))}>
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">{formatDate(task.due_date)}</span>
                  </div>
                  {daysRemaining !== null && (
                    <p className={cn('text-xs mt-0.5', getDeadlineColor(daysRemaining))}>
                      {daysRemaining < 0
                        ? `Overdue by ${Math.abs(daysRemaining)} days`
                        : daysRemaining === 0
                        ? 'Due today'
                        : `${daysRemaining} days left`}
                    </p>
                  )}
                </div>
              )}

              {/* Time logged */}
              <div>
                <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Time Logged</h4>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {task.time_logged_minutes > 0
                      ? formatDuration(task.time_logged_minutes)
                      : 'No time logged'}
                  </span>
                </div>
              </div>

              {/* Assignee */}
              {task.assignee_name && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Assigned To</h4>
                  <div className="flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    <span className="text-sm">{task.assignee_name}</span>
                  </div>
                </div>
              )}

              {/* Client */}
              {task.client_name && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Client</h4>
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-4 w-4" />
                    <span className="text-sm">{task.client_name}</span>
                  </div>
                </div>
              )}

              {/* Statutory type */}
              {task.statutory_type && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Return Type</h4>
                  <span className="text-sm px-2 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400">
                    {task.statutory_type}
                  </span>
                </div>
              )}

              {/* Priority */}
              <div>
                <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/70 mb-1">Priority</h4>
                <span
                  className={cn(
                    'text-sm px-2 py-0.5 rounded',
                    task.priority === 'urgent' && 'bg-red-500/10 text-red-600 dark:text-red-400',
                    task.priority === 'high' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                    task.priority === 'medium' && 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
                    task.priority === 'low' && 'bg-muted text-muted-foreground'
                  )}
                >
                  {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Actions</h4>

              {/* Timer button */}
              {task.status !== 'completed' && (
                <Button
                  onClick={handleStartTimer}
                  disabled={isTimerActive}
                  className={isTimerActive ? "w-full" : "w-full bg-gradient-to-r from-primary to-[hsl(280,100%,70%)] hover:opacity-90"}
                  variant={isTimerActive ? 'secondary' : 'default'}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {isTimerActive ? 'Timer Running' : 'Start Timer'}
                </Button>
              )}

              {/* Status change buttons */}
              <div className="flex flex-wrap gap-2">
                {statusOptions
                  .filter((opt) => opt.value !== task.status)
                  .map((opt) => (
                    <Button
                      key={opt.value}
                      variant="outline"
                      size="sm"
                      className="transition-all"
                      onClick={() => handleStatusChange(opt.value)}
                      disabled={changeStatus.isPending}
                    >
                      {changeStatus.isPending ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : opt.value === 'completed' ? (
                        <Check className="h-3 w-3 mr-1" />
                      ) : null}
                      Mark as {opt.label}
                    </Button>
                  ))}
              </div>
            </div>

            {/* Timestamps */}
            <div className="pt-4 border-t text-xs text-muted-foreground">
              <p>Created: {formatDate(task.created_at)}</p>
              <p>Updated: {formatDate(task.updated_at)}</p>
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
